// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 63.24.0 (D1) — a procedure's pack-declared lifecycle, and the one
 * shape check both sides of the pipeline run.
 *
 * ## Why one function
 *
 * `scripts/validate-pack-content.ts` refuses a malformed declaration at pack
 * time; `readProcedureLifecycle` reads the installed record at runtime. If the
 * two kept their own rules they would disagree about one record eventually,
 * which is how `validateTransitionalRelationship` came to be shared between the
 * pack validator and the scheme registry. The rules that need the rest of the
 * pack (a deadline id that exists, a `linked_procedure_id` that agrees) stay in
 * the validator, because the installed procedure does not carry the deadline
 * file.
 *
 * ## What it refuses
 *
 * - a `lifecycle_states` that is not a non-empty array, or a state that is not
 *   an object with a `state_id`, a `label` and a `source_ref`;
 * - a duplicate `state_id` within the procedure;
 * - a `label` that is not a locale map carrying non-empty `en` and `vi` (a bare
 *   string is refused: it would render unlocalised for one of the two locales);
 * - a clock link with no `deadline_id`, or whose start, end, or paused state is
 *   not a state of this procedure;
 * - a clock link whose `applies_when.form_ids` names a form the procedure does
 *   not list in `related_form_ids`;
 * - a clock link whose `applies_when.state_entered` names no state of the
 *   procedure, or whose dates are not in the same form as below;
 * - a clock link whose `applies_when.start_state_entered` is not a pair of
 *   `YYYY-MM-DD` dates, or whose `before` is not after its `on_or_after` (such a
 *   link could never apply);
 * - `clock_links` with no `lifecycle_states` to refer to.
 *
 * ## R-46 and R-43
 *
 * Nothing here names an instrument, a state or a clock. The vocabulary (`en`,
 * `vi`, `state_id`) is the record's own. No existing lifecycle or state-machine
 * helper was found (REUSE.md); the closest pattern is `resolvePersonaRelevance`,
 * which this reader copies in tolerance: the installed record is data.
 */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNonEmptyString = (v) => typeof v === 'string' && v.trim() !== '';
const isIsoDay = (v) => {
    if (typeof v !== 'string' || !ISO_DAY.test(v))
        return false;
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
/** Returns each problem as a sentence. An empty array means the declaration is well-formed. */
export function findProcedureLifecycleShapeErrors(procedure) {
    if (!isObject(procedure))
        return ['procedure is not an object'];
    const hasStates = procedure.lifecycle_states !== undefined;
    const hasLinks = procedure.clock_links !== undefined;
    if (!hasStates) {
        if (hasLinks)
            return ['clock_links declared with no lifecycle_states to refer to'];
        return [
            'transitions',
            'initial_state_ids',
            'lifecycle_events',
            'clock_groups',
            'recording_scope',
            'closure_anchor_links',
            'stop_proceeding_ref',
        ].some((key) => procedure[key] !== undefined)
            ? ['recording declarations require lifecycle_states']
            : [];
    }
    const errors = [];
    if (procedure.stop_proceeding_ref !== undefined) {
        const ref = procedure.stop_proceeding_ref;
        if (!isObject(ref) ||
            Object.keys(ref).some((key) => !['procedure_id', 'receipt_event_id', 'source_ref'].includes(key)) ||
            !isNonEmptyString(ref.procedure_id) ||
            !isNonEmptyString(ref.receipt_event_id) ||
            !isNonEmptyString(ref.source_ref))
            errors.push('stop_proceeding_ref must declare only procedure_id, receipt_event_id and source_ref');
    }
    if (procedure.closure_anchor_links !== undefined) {
        const links = procedure.closure_anchor_links;
        const seen = new Set();
        const events = Array.isArray(procedure.lifecycle_events) ? procedure.lifecycle_events : [];
        if (!Array.isArray(links) || links.length === 0)
            errors.push('closure_anchor_links must be a non-empty array');
        else
            links.forEach((link, i) => {
                const at = `closure_anchor_links[${i}]`;
                if (!isObject(link) ||
                    Object.keys(link).some((key) => !['anchor', 'event_id', 'source_ref'].includes(key)) ||
                    typeof link.anchor !== 'string' ||
                    !['stop_decision', 'settlement_completion'].includes(link.anchor) ||
                    !isNonEmptyString(link.event_id) ||
                    !isNonEmptyString(link.source_ref)) {
                    errors.push(`${at} must declare only a known anchor, event_id and source_ref`);
                    return;
                }
                if (seen.has(link.anchor))
                    errors.push(`${at}.anchor is declared twice`);
                seen.add(link.anchor);
                if (!events.some((event) => isObject(event) && event.event_id === link.event_id))
                    errors.push(`${at}.event_id is not declared on this procedure`);
            });
    }
    if (procedure.application_form_ids !== undefined) {
        const forms = procedure.application_form_ids;
        const related = Array.isArray(procedure.related_form_ids) ? procedure.related_form_ids : [];
        if (!Array.isArray(forms) ||
            forms.length === 0 ||
            forms.some((id) => typeof id !== 'string' || !id.trim() || !related.includes(id)) ||
            new Set(forms).size !== forms.length) {
            errors.push('application_form_ids must be a non-empty unique subset of related_form_ids');
        }
    }
    if (procedure.filing_requirements !== undefined) {
        const filing = procedure.filing_requirements;
        if (!isObject(filing) ||
            Object.keys(filing).some((key) => !['customer_group_required', 'results_report_required'].includes(key)) ||
            typeof filing.customer_group_required !== 'boolean' ||
            typeof filing.results_report_required !== 'boolean') {
            errors.push('filing_requirements must declare only customer_group_required and results_report_required booleans');
        }
    }
    const states = procedure.lifecycle_states;
    if (!Array.isArray(states) || states.length === 0) {
        return ['lifecycle_states must be a non-empty array'];
    }
    const stateIds = new Set();
    states.forEach((raw, i) => {
        const at = `lifecycle_states[${i}]`;
        if (!isObject(raw)) {
            errors.push(`${at} is not an object`);
            return;
        }
        const s = raw;
        if (!isNonEmptyString(s.state_id)) {
            errors.push(`${at}.state_id must be a non-empty string`);
        }
        else if (stateIds.has(s.state_id)) {
            errors.push(`${at}.state_id "${s.state_id}" is declared twice`);
        }
        else {
            stateIds.add(s.state_id);
        }
        if (!isNonEmptyString(s.source_ref)) {
            errors.push(`${at}.source_ref must name the article, point or form the text cites`);
        }
        if (s.is_grant !== undefined && typeof s.is_grant !== 'boolean') {
            errors.push(`${at}.is_grant must be true or false when it is stated`);
        }
        const label = s.label;
        if (!isObject(label)) {
            errors.push(`${at}.label must be a locale map with en and vi, not a bare string`);
        }
        else {
            for (const locale of ['en', 'vi']) {
                if (!isNonEmptyString(label[locale])) {
                    errors.push(`${at}.label.${locale} is missing or empty`);
                }
            }
        }
    });
    errors.push(...recordingShapeErrors(procedure, stateIds));
    const clocks = procedure.clock_links;
    if (clocks === undefined)
        return errors;
    if (!Array.isArray(clocks)) {
        errors.push('clock_links must be an array');
        return errors;
    }
    const formIds = Array.isArray(procedure.related_form_ids)
        ? new Set(procedure.related_form_ids.filter((x) => typeof x === 'string'))
        : new Set();
    clocks.forEach((raw, i) => {
        const at = `clock_links[${i}]`;
        if (!isObject(raw)) {
            errors.push(`${at} is not an object`);
            return;
        }
        const c = raw;
        for (const key of Object.keys(c))
            if (![
                'deadline_id',
                'starts_at_state_id',
                'ends_at_state_id',
                'paused_in_state_ids',
                'applies_when',
                'clock_group',
                'starts_at_event_id',
                'ends_at_event_ids',
                'pause_events',
                'requires_recording_scope',
                'anchor_review_status',
                'anchor_review_reason',
                'anchor_review_note',
                'start_date_from_window',
            ].includes(key))
                errors.push(`${at}.${key} is not a supported clock-link field`);
        const eventIds = new Set(Array.isArray(procedure.lifecycle_events)
            ? procedure.lifecycle_events.filter(isObject).map((event) => event.event_id)
            : []);
        const groups = Array.isArray(procedure.clock_groups) ? procedure.clock_groups : [];
        if (c.requires_recording_scope !== undefined && typeof c.requires_recording_scope !== 'boolean')
            errors.push(`${at}.requires_recording_scope must be boolean`);
        if (c.anchor_review_status !== undefined &&
            c.anchor_review_status !== 'verified' &&
            c.anchor_review_status !== 'review_required')
            errors.push(`${at}.anchor_review_status is unsupported`);
        if (c.anchor_review_status === 'review_required' && !isNonEmptyString(c.anchor_review_reason))
            errors.push(`${at}.anchor_review_reason is required`);
        if (c.anchor_review_note !== undefined &&
            (!isObject(c.anchor_review_note) ||
                !isNonEmptyString(c.anchor_review_note.en) ||
                !isNonEmptyString(c.anchor_review_note.vi)))
            errors.push(`${at}.anchor_review_note must author en and vi`);
        if (c.start_date_from_window !== undefined &&
            (!isObject(c.start_date_from_window) ||
                !eventIds.has(c.start_date_from_window.event_id) ||
                !isNonEmptyString(c.start_date_from_window.deadline_id))) {
            errors.push(`${at}.start_date_from_window must name a declared event and deadline`);
        }
        if (isObject(c.start_date_from_window) &&
            Object.keys(c.start_date_from_window).some((key) => key !== 'event_id' && key !== 'deadline_id'))
            errors.push(`${at}.start_date_from_window has an unsupported field`);
        if (c.clock_group !== undefined && !groups.includes(c.clock_group)) {
            errors.push(`${at}.clock_group is not a declared clock group`);
        }
        if (c.starts_at_event_id !== undefined && !eventIds.has(c.starts_at_event_id)) {
            errors.push(`${at}.starts_at_event_id is not a declared lifecycle event`);
        }
        if (c.ends_at_event_ids !== undefined &&
            (!Array.isArray(c.ends_at_event_ids) || c.ends_at_event_ids.some((id) => !eventIds.has(id)))) {
            errors.push(`${at}.ends_at_event_ids must name declared lifecycle events`);
        }
        if (c.pause_events !== undefined &&
            (!Array.isArray(c.pause_events) ||
                c.pause_events.some((pair) => !isObject(pair) ||
                    !eventIds.has(pair.starts_at_event_id) ||
                    !eventIds.has(pair.ends_at_event_id) ||
                    Object.keys(pair).some((key) => key !== 'starts_at_event_id' && key !== 'ends_at_event_id')))) {
            errors.push(`${at}.pause_events must name declared start and end events`);
        }
        if (!isNonEmptyString(c.deadline_id))
            errors.push(`${at}.deadline_id must be a non-empty string`);
        if (!isNonEmptyString(c.starts_at_state_id) || !stateIds.has(c.starts_at_state_id)) {
            errors.push(`${at}.starts_at_state_id is not a state of this procedure`);
        }
        if (c.ends_at_state_id !== null &&
            (!isNonEmptyString(c.ends_at_state_id) || !stateIds.has(c.ends_at_state_id))) {
            errors.push(`${at}.ends_at_state_id must be a state of this procedure or null`);
        }
        if (c.paused_in_state_ids !== undefined) {
            const paused = c.paused_in_state_ids;
            if (!Array.isArray(paused) || paused.some((p) => !isNonEmptyString(p) || !stateIds.has(p))) {
                errors.push(`${at}.paused_in_state_ids must name only states of this procedure`);
            }
        }
        if (c.applies_when !== undefined) {
            const a = c.applies_when;
            if (!isObject(a)) {
                errors.push(`${at}.applies_when must be an object`);
                return;
            }
            for (const key of Object.keys(a))
                if (!['form_ids', 'state_entered', 'start_state_entered'].includes(key)) {
                    errors.push(`${at}.applies_when.${key} is not a supported applicability field`);
                }
            if (a.form_ids !== undefined) {
                const ids = a.form_ids;
                if (!Array.isArray(ids) || ids.length === 0) {
                    errors.push(`${at}.applies_when.form_ids must be a non-empty array`);
                }
                else {
                    for (const f of ids) {
                        if (!isNonEmptyString(f) || !formIds.has(f)) {
                            errors.push(`${at}.applies_when.form_ids names "${String(f)}", which is not in related_form_ids`);
                        }
                    }
                }
            }
            if (a.state_entered !== undefined) {
                const w = a.state_entered;
                if (!isObject(w)) {
                    errors.push(`${at}.applies_when.state_entered must be an object`);
                }
                else {
                    for (const key of Object.keys(w))
                        if (!['state_id', 'on_or_after', 'before'].includes(key)) {
                            errors.push(`${at}.applies_when.state_entered.${key} is not a supported applicability field`);
                        }
                    if (!isNonEmptyString(w.state_id) || !stateIds.has(w.state_id)) {
                        errors.push(`${at}.applies_when.state_entered.state_id is not a state of this procedure`);
                    }
                    const onOrAfter = w.on_or_after;
                    const before = w.before;
                    if (onOrAfter === undefined && before === undefined) {
                        errors.push(`${at}.applies_when.state_entered states neither bound`);
                    }
                    if (onOrAfter !== undefined && !isIsoDay(onOrAfter)) {
                        errors.push(`${at}.applies_when.state_entered.on_or_after must be a YYYY-MM-DD date`);
                    }
                    if (before !== undefined && !isIsoDay(before)) {
                        errors.push(`${at}.applies_when.state_entered.before must be a YYYY-MM-DD date`);
                    }
                    if (isIsoDay(onOrAfter) && isIsoDay(before) && before <= onOrAfter) {
                        errors.push(`${at}.applies_when.state_entered can never hold: before (${before}) is not after on_or_after (${onOrAfter})`);
                    }
                }
            }
            if (a.start_state_entered !== undefined) {
                const w = a.start_state_entered;
                if (!isObject(w)) {
                    errors.push(`${at}.applies_when.start_state_entered must be an object`);
                }
                else {
                    for (const key of Object.keys(w))
                        if (!['on_or_after', 'before'].includes(key)) {
                            errors.push(`${at}.applies_when.start_state_entered.${key} is not a supported applicability field`);
                        }
                    const onOrAfter = w.on_or_after;
                    const before = w.before;
                    if (onOrAfter === undefined && before === undefined) {
                        errors.push(`${at}.applies_when.start_state_entered states neither bound`);
                    }
                    if (onOrAfter !== undefined && !isIsoDay(onOrAfter)) {
                        errors.push(`${at}.applies_when.start_state_entered.on_or_after must be a YYYY-MM-DD date`);
                    }
                    if (before !== undefined && !isIsoDay(before)) {
                        errors.push(`${at}.applies_when.start_state_entered.before must be a YYYY-MM-DD date`);
                    }
                    if (isIsoDay(onOrAfter) && isIsoDay(before) && before <= onOrAfter) {
                        errors.push(`${at}.applies_when.start_state_entered can never hold: before (${before}) is not after on_or_after (${onOrAfter})`);
                    }
                }
            }
        }
    });
    return errors;
}
/** The documentary graph and fact vocabulary share this checker at install and runtime. */
function recordingShapeErrors(procedure, stateIds) {
    const errors = [];
    const keysOnly = (row, keys, at) => {
        for (const key of Object.keys(row))
            if (!keys.includes(key))
                errors.push(`${at}.${key} is not a supported recording field`);
    };
    const groups = procedure.clock_groups;
    if (groups !== undefined &&
        (!Array.isArray(groups) ||
            groups.some((g) => !isNonEmptyString(g)) ||
            new Set(groups).size !== groups.length))
        errors.push('clock_groups must contain unique non-empty strings');
    const knownGroups = new Set(Array.isArray(groups) ? groups : []);
    const events = procedure.lifecycle_events;
    if (events !== undefined && !Array.isArray(events))
        errors.push('lifecycle_events must be an array');
    const eventIds = new Set();
    const rows = Array.isArray(events) ? events : [];
    for (const [i, event] of rows.entries()) {
        if (!isObject(event) || !isNonEmptyString(event.event_id)) {
            errors.push(`lifecycle_events[${i}].event_id must be a non-empty string`);
        }
        else if (eventIds.has(event.event_id))
            errors.push(`lifecycle_events[${i}].event_id is duplicated`);
        else
            eventIds.add(event.event_id);
    }
    const refs = (value, at, known) => {
        if (!Array.isArray(value) ||
            value.length === 0 ||
            value.some((id) => !isNonEmptyString(id) || !known.has(id)) ||
            new Set(value).size !== value.length)
            errors.push(`${at} must name unique declared identifiers`);
    };
    const dependencies = new Map();
    for (const [i, event] of rows.entries()) {
        const at = `lifecycle_events[${i}]`;
        if (!isObject(event))
            continue;
        keysOnly(event, [
            'event_id',
            'label',
            'source_ref',
            'actor',
            'max_occurrences',
            'requires_event_ids',
            'requires_any_event_ids',
            'date_constraints',
            'ends_clock_groups',
            'requires_recording_scope',
        ], at);
        for (const field of ['actor', 'source_ref'])
            if (!isNonEmptyString(event[field]))
                errors.push(`${at}.${field} is required`);
        if (event.requires_recording_scope !== undefined &&
            typeof event.requires_recording_scope !== 'boolean')
            errors.push(`${at}.requires_recording_scope must be boolean`);
        if (!isObject(event.label) ||
            !isNonEmptyString(event.label.en) ||
            !isNonEmptyString(event.label.vi)) {
            errors.push(`${at}.label must author en and vi`);
        }
        if (event.max_occurrences !== undefined &&
            (!Number.isInteger(event.max_occurrences) || event.max_occurrences < 1))
            errors.push(`${at}.max_occurrences must be a positive integer`);
        const deps = [];
        if (event.requires_event_ids !== undefined) {
            refs(event.requires_event_ids, `${at}.requires_event_ids`, eventIds);
            if (Array.isArray(event.requires_event_ids))
                deps.push(...event.requires_event_ids.filter(isNonEmptyString));
        }
        if (event.requires_any_event_ids !== undefined) {
            refs(event.requires_any_event_ids, `${at}.requires_any_event_ids`, eventIds);
            if (Array.isArray(event.requires_any_event_ids))
                deps.push(...event.requires_any_event_ids.filter(isNonEmptyString));
        }
        if (event.ends_clock_groups !== undefined)
            refs(event.ends_clock_groups, `${at}.ends_clock_groups`, knownGroups);
        if (event.date_constraints !== undefined) {
            if (!Array.isArray(event.date_constraints))
                errors.push(`${at}.date_constraints must be an array`);
            else
                for (const [j, rule] of event.date_constraints.entries()) {
                    const path = `${at}.date_constraints[${j}]`;
                    if (!isObject(rule)) {
                        errors.push(`${path} must be an object`);
                        continue;
                    }
                    keysOnly(rule, ['event_id', 'relation', 'deadline_id', 'absent_event_id'], path);
                    if (!isNonEmptyString(rule.event_id) || !eventIds.has(rule.event_id))
                        errors.push(`${path}.event_id is not declared`);
                    else
                        deps.push(rule.event_id);
                    if (rule.relation !== 'on_or_after' && rule.relation !== 'after_window')
                        errors.push(`${path}.relation is unsupported`);
                    if (rule.relation === 'after_window' && !isNonEmptyString(rule.deadline_id))
                        errors.push(`${path}.deadline_id is required`);
                    if (rule.relation === 'on_or_after' &&
                        (rule.deadline_id !== undefined || rule.absent_event_id !== undefined)) {
                        errors.push(`${path} cannot declare a window or absence without after_window`);
                    }
                    if (rule.absent_event_id !== undefined &&
                        (!isNonEmptyString(rule.absent_event_id) || !eventIds.has(rule.absent_event_id))) {
                        errors.push(`${path}.absent_event_id is not declared`);
                    }
                }
        }
        if (isNonEmptyString(event.event_id))
            dependencies.set(event.event_id, deps);
    }
    const active = new Set();
    const done = new Set();
    const visit = (id) => {
        if (active.has(id)) {
            errors.push(`lifecycle_events prerequisite cycle at ${id}`);
            return;
        }
        if (done.has(id))
            return;
        active.add(id);
        for (const dependency of dependencies.get(id) ?? [])
            visit(dependency);
        active.delete(id);
        done.add(id);
    };
    for (const id of dependencies.keys())
        visit(id);
    if (procedure.recording_scope !== undefined) {
        const scope = procedure.recording_scope;
        if (!isObject(scope))
            errors.push('recording_scope must be an object');
        else {
            keysOnly(scope, ['anchor_event_id', 'anchor_state_id', 'periods'], 'recording_scope');
            if (!isNonEmptyString(scope.anchor_event_id) || !eventIds.has(scope.anchor_event_id))
                errors.push('recording_scope.anchor_event_id is not declared');
            if (!isNonEmptyString(scope.anchor_state_id) || !stateIds.has(scope.anchor_state_id))
                errors.push('recording_scope.anchor_state_id is not declared');
            if (!Array.isArray(scope.periods) || scope.periods.length === 0)
                errors.push('recording_scope.periods must be nonempty');
            else {
                const ranges = [];
                for (const [i, period] of scope.periods.entries()) {
                    if (!isObject(period)) {
                        errors.push(`recording_scope.periods[${i}] must be an object`);
                        continue;
                    }
                    keysOnly(period, ['on_or_after', 'before', 'status', 'source_ref'], `recording_scope.periods[${i}]`);
                    if (period.on_or_after !== undefined && !isIsoDay(period.on_or_after))
                        errors.push(`recording_scope.periods[${i}].on_or_after must be a calendar date`);
                    if (period.before !== undefined && !isIsoDay(period.before))
                        errors.push(`recording_scope.periods[${i}].before must be a calendar date`);
                    if (period.status !== 'verified' && period.status !== 'review_required')
                        errors.push(`recording_scope.periods[${i}].status is unsupported`);
                    if (!isNonEmptyString(period.source_ref))
                        errors.push(`recording_scope.periods[${i}].source_ref is required`);
                    const from = isIsoDay(period.on_or_after) ? period.on_or_after : '';
                    // An absent upper boundary sorts after every ISO day; it is not a date.
                    const to = isIsoDay(period.before) ? period.before : '\uffff';
                    if (from >= to)
                        errors.push(`recording_scope.periods[${i}] is empty or reversed`);
                    if (ranges.some((range) => from < range.to && to > range.from))
                        errors.push(`recording_scope.periods[${i}] overlaps another period`);
                    ranges.push({ from, to });
                }
            }
        }
    }
    if (procedure.transitions === undefined) {
        if (procedure.initial_state_ids !== undefined)
            errors.push('initial_state_ids declared without transitions');
        return errors;
    }
    if (!Array.isArray(procedure.transitions) || procedure.transitions.length === 0) {
        errors.push('transitions must be a non-empty array');
        return errors;
    }
    refs(procedure.initial_state_ids, 'initial_state_ids', stateIds);
    const initial = new Set();
    const edgeIds = new Set();
    for (const [i, edge] of procedure.transitions.entries()) {
        const at = `transitions[${i}]`;
        if (!isObject(edge)) {
            errors.push(`${at} must be an object`);
            continue;
        }
        keysOnly(edge, [
            'from_state_id',
            'to_state_id',
            'source_ref',
            'actor',
            'requires_event_ids',
            'requires_any_event_ids',
            'requires_recording_scope',
            'forbids_event_ids',
        ], at);
        if (edge.from_state_id !== null &&
            (!isNonEmptyString(edge.from_state_id) || !stateIds.has(edge.from_state_id)))
            errors.push(`${at}.from_state_id is not a state or null`);
        if (!isNonEmptyString(edge.to_state_id) || !stateIds.has(edge.to_state_id))
            errors.push(`${at}.to_state_id is not a state`);
        else if (edge.from_state_id === null)
            initial.add(edge.to_state_id);
        for (const field of ['actor', 'source_ref'])
            if (!isNonEmptyString(edge[field]))
                errors.push(`${at}.${field} is required`);
        if (edge.requires_recording_scope !== undefined &&
            typeof edge.requires_recording_scope !== 'boolean')
            errors.push(`${at}.requires_recording_scope must be boolean`);
        for (const field of ['requires_event_ids', 'requires_any_event_ids', 'forbids_event_ids'])
            if (edge[field] !== undefined)
                refs(edge[field], `${at}.${field}`, eventIds);
        const forbidden = new Set(Array.isArray(edge.forbids_event_ids) ? edge.forbids_event_ids : []);
        if ((Array.isArray(edge.requires_event_ids) &&
            edge.requires_event_ids.some((id) => forbidden.has(id))) ||
            (Array.isArray(edge.requires_any_event_ids) &&
                edge.requires_any_event_ids.length > 0 &&
                edge.requires_any_event_ids.every((id) => forbidden.has(id)))) {
            errors.push(`${at} cannot both require and forbid its prerequisite facts`);
        }
        const key = JSON.stringify([edge.from_state_id, edge.to_state_id]);
        if (edgeIds.has(key))
            errors.push(`${at} duplicates a transition edge`);
        edgeIds.add(key);
    }
    const authored = new Set(Array.isArray(procedure.initial_state_ids) ? procedure.initial_state_ids : []);
    if (authored.size !== initial.size || [...initial].some((id) => !authored.has(id)))
        errors.push('initial_state_ids must equal the targets of null-from transitions');
    return errors;
}
/**
 * Reads a procedure's lifecycle from its installed record. A procedure that
 * declares no `lifecycle_states` reads `absent`; one whose declaration fails the
 * shape check reads `malformed` with the reasons, not as absent. Clock links are
 * returned with `paused_in_state_ids` always present (empty when none), so a
 * consumer never has to test for the field.
 */
export function readProcedureLifecycle(procedure) {
    if (!isObject(procedure))
        return { status: 'absent' };
    if ([
        'lifecycle_states',
        'clock_links',
        'transitions',
        'initial_state_ids',
        'lifecycle_events',
        'clock_groups',
        'recording_scope',
    ].every((key) => procedure[key] === undefined)) {
        return { status: 'absent' };
    }
    const problems = findProcedureLifecycleShapeErrors(procedure);
    if (problems.length > 0)
        return { status: 'malformed', problems };
    const states = procedure.lifecycle_states;
    const links = (procedure.clock_links ?? []);
    return {
        status: 'declared',
        states,
        clockLinks: links.map((c) => ({ ...c, paused_in_state_ids: c.paused_in_state_ids ?? [] })),
        transitions: procedure.transitions ?? null,
        events: procedure.lifecycle_events ?? [],
        clockGroups: procedure.clock_groups ?? [],
        initialStateIds: procedure.initial_state_ids ?? [],
    };
}
//# sourceMappingURL=procedure-lifecycle.js.map